import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AddressField,
  Alert,
  AttachmentField,
  Button,
  DateField,
  Field,
  Form,
  FormFooter,
  FormRow,
  FormSection,
  SearchSelect,
  TextArea,
  TimeField,
  useAttachments,
  type Address,
  type FormSectionStatus,
} from '@evogard/ui';
import type { ClaimFields } from '@evogard/contract';
import {
  asksThirdPartyNotices,
  businessDate,
  HOUSE_TIMEZONE,
  startOfDay,
  thirdPartyQuestions,
} from '@evogard/domain';
import { geocodeAddress } from '../../../lib/geocode.js';
import {
  ThirdPartyVehicleField,
  type ThirdPartyVehicleValue,
} from '../third-parties/ThirdPartyVehicleField.js';
import { ErpVehicleField } from './ErpVehicleField.js';
import {
  CAMERAS_OPTIONS,
  CAUSED_BY_OPTIONS,
  CLAIM_TYPE_OPTIONS,
  REPORTED_THIRD_PARTIES_OPTIONS,
  THIRD_PARTY_DAMAGE_OPTIONS,
  TOW_DESTINATION_OPTIONS,
  YES_NO,
  type Option,
} from './labels.js';
import {
  archiveNoticeFile,
  noticeAttachmentItem,
  uploadNoticeFiles,
} from './noticeFiles.js';
import { useClaimMutations, useClaimPermissions, type Claim } from './useClaims.js';

/**
 * O formulário do sinistro — abrir o do associado, abrir o do terceiro, e
 * corrigir. Os campos são os do formulário de abertura do pipe "1 - Aviso de
 * Sinistro" (`notice.json`), agrupados pelo que perguntam.
 *
 * ## Três modos, uma forma
 *
 * - **`associado`**: o carro vem do espelho do SGA (`ErpVehicleField`).
 * - **`terceiro`**: nasce da ficha do sinistro do associado, que é o pai
 *   (`parent_claim_id`). Vem do pai, e continua editável, o que é do MESMO
 *   atendimento: o protocolo, o tipo, quando ocorreu e quando foi avisado, o
 *   endereço inteiro e o reboque. Começa vazio o que é a VERSÃO do terceiro
 *   (decisão do dono, 28/09): a descrição, o sentido, as câmeras, a dinâmica e
 *   o que ele declara sobre terceiros. O carro vem do `ThirdPartyVehicleField`.
 *   **O condutor não é perguntado** (decisão do dono, 05/10): a seção não
 *   aparece, nem ao abrir nem ao corrigir, e o corpo vai sem os campos.
 * - **Os terceiros** (decisão do dono, 05/10): o aviso do associado pergunta
 *   só "Envolve terceiros?" e, no "Sim", avisa que cada terceiro abre o
 *   próprio aviso. É no aviso do terceiro que se responde se envolve veículo,
 *   o seguro e o tipo de dano — um carro e um muro no mesmo acionamento são
 *   dois terceiros, com respostas diferentes. Sem veículo, o aviso aponta só
 *   para a pessoa (o dono do muro).
 * - **`editar`**: corrige o que foi contado. O tipo e o pai não mudam (trocá-los
 *   seria outro sinistro); o veículo pode ser trocado.
 *
 * ## Obrigatório é o que o contrato exige
 *
 * O botão só habilita quando os obrigatórios do contrato estão preenchidos —
 * a tela antecipa a mesma recusa da API. Os campos que só fazem sentido depois
 * de outra resposta (o condutor quando não é o associado, a descrição do órgão
 * quando houve órgão) só aparecem então.
 *
 * ## As imagens do ocorrido
 *
 * Foto, vídeo ou qualquer arquivo — decisão do dono em 05/10. A etapa vem
 * logo depois da ocorrência, porque as imagens são do sinistro e se leem
 * junto do relato (pedido do dono, 05/10). É o
 * `AttachmentField` da casa, e o arquivo sobe **depois** de o sinistro existir,
 * com o id dele (o mesmo laço do compositor das demandas). Se um arquivo falha,
 * o sinistro já está aberto: o formulário fica com o que não subiu e o botão
 * passa a reenviar só ele — clicar de novo não abre um segundo sinistro. Na
 * correção, a etapa mostra os arquivos gravados, e incluir e tirar valem quando
 * a correção é salva, como todo outro campo.
 */

type Mode =
  | { kind: 'associado' }
  | { kind: 'terceiro'; parent: Claim }
  | { kind: 'editar'; claim: Claim };

/** Os campos do condutor — só do aviso do associado. */
type DriverKey = 'driverIsMember' | 'driverDocument' | 'driverName' | 'driverPhone';

interface Values {
  driverIsMember: string;
  driverDocument: string;
  driverName: string;
  driverPhone: string;
  opaSuiteProtocol: string;
  direction: string;
  occurredDate: string;
  occurredHour: string;
  notifiedDate: string;
  notifiedHour: string;
  claimType: string;
  occurrenceHasCameras: string;
  causedBy: string;
  hadRightOfWayViolation: string;
  trafficAuthorityNotified: string;
  trafficAuthorityDescription: string;
  memberRequestedTow: string;
  towDestinationType: string;
  towDestinationAddress: string;
  description: string;
  reportedThirdPartyVehicle: string;
  reportedThirdParties: string;
  thirdPartyHasInsurance: string;
  thirdPartyDamageType: string;
}

const EMPTY: Values = {
  driverIsMember: '',
  driverDocument: '',
  driverName: '',
  driverPhone: '',
  opaSuiteProtocol: '',
  direction: '',
  occurredDate: '',
  occurredHour: '',
  notifiedDate: '',
  notifiedHour: '',
  claimType: '',
  occurrenceHasCameras: '',
  causedBy: '',
  hadRightOfWayViolation: '',
  trafficAuthorityNotified: '',
  trafficAuthorityDescription: '',
  memberRequestedTow: '',
  towDestinationType: '',
  towDestinationAddress: '',
  description: '',
  reportedThirdPartyVehicle: '',
  reportedThirdParties: '',
  thirdPartyHasInsurance: '',
  thirdPartyDamageType: '',
};

/**
 * O instante, na data e na hora **da casa** (`HOUSE_TIMEZONE`) — o mesmo fuso
 * em que `formatInstant` o mostra. Com o fuso do navegador, uma máquina fora
 * de Fortaleza gravaria uma hora e mostraria outra.
 */
function splitInstant(iso: string): { date: string; hour: string } {
  const hour = new Intl.DateTimeFormat('en-GB', {
    timeZone: HOUSE_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
  return { date: businessDate(iso), hour };
}

/** A data e a hora da casa, como instante. `null` enquanto incompleto. */
function joinInstant(date: string, hour: string): string | null {
  const m = /^(\d{2}):(\d{2})$/.exec(hour);
  if (!date || !m) return null;
  const midnight = startOfDay(date);
  return new Date(
    midnight.getTime() + (Number(m[1]) * 60 + Number(m[2])) * 60_000,
  ).toISOString();
}

/**
 * O pino do mapa, como link. A coordenada vai à parte (`latitude`/`longitude`
 * de `addresses`); o link é o que a ficha abre no mapa.
 */
function mapsLink(latitude: number | null, longitude: number | null): string | null {
  return latitude !== null && longitude !== null
    ? `https://www.google.com/maps?q=${latitude},${longitude}`
    : null;
}

/**
 * `tudo` para corrigir; `terceiro` para abrir o do terceiro a partir do
 * associado — só o que é do mesmo atendimento (ver o docblock do topo).
 */
function valuesFrom(claim: Claim, part: 'tudo' | 'terceiro'): Values {
  const occurred = splitInstant(claim.occurredAt);
  const notified = splitInstant(claim.notifiedAt);
  const inherited: Partial<Values> = {
    opaSuiteProtocol: claim.opaSuiteProtocol ?? '',
    occurredDate: occurred.date,
    occurredHour: occurred.hour,
    notifiedDate: notified.date,
    notifiedHour: notified.hour,
    claimType: claim.claimType,
    memberRequestedTow: claim.memberRequestedTow,
    towDestinationType: claim.towDestinationType,
    towDestinationAddress: claim.towDestinationAddress ?? '',
  };
  if (part === 'terceiro') return { ...EMPTY, ...inherited };
  return {
    ...EMPTY,
    ...inherited,
    direction: claim.direction ?? '',
    occurrenceHasCameras: claim.occurrenceHasCameras ?? '',
    causedBy: claim.causedBy,
    hadRightOfWayViolation: claim.hadRightOfWayViolation,
    trafficAuthorityNotified: claim.trafficAuthorityNotified,
    trafficAuthorityDescription: claim.trafficAuthorityDescription ?? '',
    description: claim.description,
    reportedThirdPartyVehicle: claim.reportedThirdPartyVehicle ?? '',
    reportedThirdParties: claim.reportedThirdParties ?? '',
    thirdPartyHasInsurance: claim.thirdPartyHasInsurance ?? '',
    thirdPartyDamageType: claim.thirdPartyDamageType ?? '',
    driverIsMember: claim.driverIsMember ?? '',
    driverDocument: claim.driverDocument ?? '',
    driverName: claim.driverName ?? '',
    driverPhone: claim.driverPhone ?? '',
  };
}

/** O local gravado (`addresses`), na forma do `AddressField`. */
function addressFrom(claim: Claim): Partial<Address> {
  const a = claim.occurrenceAddress;
  if (!a) return {};
  return {
    cep: a.zipCode ?? '',
    uf: a.state ?? '',
    localidade: a.city ?? '',
    bairro: a.neighborhood ?? '',
    logradouro: a.street ?? '',
    numero: a.number ?? '',
    complemento: a.complement ?? '',
    latitude: a.latitude,
    longitude: a.longitude,
  };
}

function Choice({
  label,
  value,
  onChange,
  options,
  required,
  help,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  required?: boolean;
  help?: ReactNode;
}) {
  /* O select da casa, e não o nativo: busca, ordem e o X que desfaz a escolha
   * — o `<select>` não tem como voltar a "nada escolhido". */
  return (
    <SearchSelect
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      help={help}
      sortable
      clearable
    />
  );
}

export function ClaimForm({
  mode,
  onSaved,
  onCancel,
  layer = 'painel',
}: {
  mode: Mode;
  onSaved: (claim: Claim) => void;
  onCancel: () => void;
  /**
   * Onde o formulário mora, que decide a camada do seletor de data e hora.
   * Dentro do `Modal` e do painel lateral (`z-50`) ele precisa ser `modal`:
   * com o padrão, o calendário abria POR BAIXO e só dava para digitar.
   */
  layer?: 'painel' | 'modal';
}) {
  const { create, update } = useClaimMutations();
  const { canCreate } = useClaimPermissions();
  const picked = useAttachments();
  const client = useQueryClient();
  /* Os gravados que a pessoa tirou na correção — saem quando ela salva. */
  const [removed, setRemoved] = useState<ReadonlySet<number>>(new Set());
  /* O sinistro que já abriu, quando um arquivo dele não subiu. */
  const [opened, setOpened] = useState<Claim | null>(null);
  const [filesBusy, setFilesBusy] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const source =
    mode.kind === 'editar' ? mode.claim : mode.kind === 'terceiro' ? mode.parent : null;

  const [values, setValues] = useState<Values>(() =>
    mode.kind === 'editar'
      ? valuesFrom(mode.claim, 'tudo')
      : mode.kind === 'terceiro'
        ? valuesFrom(mode.parent, 'terceiro')
        : EMPTY,
  );
  const [address, setAddress] = useState<Partial<Address>>(() =>
    source ? addressFrom(source) : {},
  );
  const [vehicleCode, setVehicleCode] = useState(
    mode.kind === 'editar' ? (mode.claim.vehicleCode ?? '') : '',
  );
  const [thirdPartyVehicle, setThirdPartyVehicle] = useState<ThirdPartyVehicleValue>(
    () =>
      mode.kind === 'editar' && mode.claim.thirdPartyId !== null
        ? {
            thirdPartyId: mode.claim.thirdPartyId,
            thirdPartyVehicleId: mode.claim.thirdPartyVehicleId,
          }
        : { thirdPartyId: null, thirdPartyVehicleId: null },
  );

  const set = (key: keyof Values) => (value: string) =>
    setValues((v) => ({ ...v, [key]: value }));
  const type = mode.kind === 'editar' ? mode.claim.type : mode.kind;

  const occurredAt = joinInstant(values.occurredDate, values.occurredHour);
  const notifiedAt = joinInstant(values.notifiedDate, values.notifiedHour);
  /* O aviso do terceiro não pergunta o condutor (ver o docblock do topo). */
  const asksDriver = type !== 'terceiro';
  const driverIsOther = values.driverIsMember === 'nao';
  const driver: Partial<ClaimFields> = asksDriver
    ? {
        driverIsMember: values.driverIsMember as ClaimFields['driverIsMember'],
        driverDocument: driverIsOther ? values.driverDocument.trim() || null : null,
        driverName: driverIsOther ? values.driverName.trim() || null : null,
        driverPhone: driverIsOther ? values.driverPhone.trim() || null : null,
      }
    : {};

  /* Cada tipo responde o seu sobre terceiros (ver o docblock do topo). */
  const asks = thirdPartyQuestions(values);
  const thirdPartyFields: Partial<ClaimFields> =
    type === 'terceiro'
      ? {
          reportedThirdPartyVehicle:
            values.reportedThirdPartyVehicle as ClaimFields['reportedThirdPartyVehicle'],
          /* O que a tela não pergunta vai nulo — a resposta de antes da troca
             não sobrevive escondida. */
          thirdPartyHasInsurance: asks.asksInsurance
            ? ((values.thirdPartyHasInsurance ||
                null) as ClaimFields['thirdPartyHasInsurance'])
            : null,
          thirdPartyDamageType: asks.asksDamageType
            ? values.thirdPartyDamageType || null
            : null,
        }
      : {
          reportedThirdParties:
            values.reportedThirdParties as ClaimFields['reportedThirdParties'],
        };
  const thirdPartyVehicleId = asks.asksVehicle
    ? thirdPartyVehicle.thirdPartyVehicleId
    : null;
  const fields: Partial<ClaimFields> = {
    opaSuiteProtocol: values.opaSuiteProtocol.trim(),
    direction: values.direction.trim() || null,
    occurredAt: occurredAt ?? '',
    notifiedAt: notifiedAt ?? '',
    claimType: values.claimType,
    occurrenceAddress: {
      zipCode: address.cep?.replace(/\D/g, '') || null,
      state: address.uf ?? '',
      city: address.localidade?.trim() ?? '',
      neighborhood: address.bairro?.trim() || null,
      street: address.logradouro?.trim() ?? '',
      number: address.numero?.trim() || null,
      complement: address.complemento?.trim() || null,
      referencePoint: null,
      locationLink: mapsLink(address.latitude ?? null, address.longitude ?? null),
      latitude: address.latitude ?? null,
      longitude: address.longitude ?? null,
    },
    occurrenceHasCameras: (values.occurrenceHasCameras ||
      null) as ClaimFields['occurrenceHasCameras'],
    causedBy: values.causedBy as ClaimFields['causedBy'],
    hadRightOfWayViolation:
      values.hadRightOfWayViolation as ClaimFields['hadRightOfWayViolation'],
    trafficAuthorityNotified:
      values.trafficAuthorityNotified as ClaimFields['trafficAuthorityNotified'],
    trafficAuthorityDescription:
      values.trafficAuthorityNotified === 'sim'
        ? values.trafficAuthorityDescription.trim() || null
        : null,
    memberRequestedTow: values.memberRequestedTow as ClaimFields['memberRequestedTow'],
    towDestinationType: values.towDestinationType as ClaimFields['towDestinationType'],
    towDestinationAddress:
      values.towDestinationType === 'outro_local_indicado_pelo_associado'
        ? values.towDestinationAddress.trim() || null
        : null,
    description: values.description.trim(),
  };

  /*
   * Os obrigatórios, POR SEÇÃO — a mesma lista que decide se o envio passa, e
   * é dela que sai o selo de cada etapa e o trilho do lado. Um obrigatório
   * novo entra aqui uma vez e aparece nos três lugares; escrito em dois, o
   * selo diria "Completo" e o botão recusaria.
   */
  const required = {
    vehicle: (type === 'terceiro'
      ? {
          'se envolve veículo': Boolean(values.reportedThirdPartyVehicle),
          'o terceiro': thirdPartyVehicle.thirdPartyId !== null,
          ...(asks.asksDamageType
            ? { 'o tipo de dano ao terceiro': Boolean(values.thirdPartyDamageType) }
            : {}),
        }
      : { 'o veículo': Boolean(vehicleCode.trim()) }) as Record<string, boolean>,
    occurrence: {
      'o protocolo OPA': Boolean(fields.opaSuiteProtocol),
      'o tipo': Boolean(values.claimType),
      'quando ocorreu': Boolean(occurredAt),
      'quando foi avisado': Boolean(notifiedAt),
      'a descrição': Boolean(fields.description),
    },
    place: {
      'o estado': fields.occurrenceAddress?.state?.length === 2,
      'a cidade': Boolean(fields.occurrenceAddress?.city),
      'o logradouro': Boolean(fields.occurrenceAddress?.street),
    },
    driver: (asksDriver
      ? { 'quem conduzia': Boolean(values.driverIsMember) }
      : {}) as Record<string, boolean>,
    dynamics: {
      'o causador': Boolean(values.causedBy),
      'o avanço de preferencial': Boolean(values.hadRightOfWayViolation),
      'se houve órgão de trânsito': Boolean(values.trafficAuthorityNotified),
    },
    tow: {
      'se houve reboque': Boolean(values.memberRequestedTow),
      'o destino do reboque': Boolean(values.towDestinationType),
    },
    thirdParties: (type === 'terceiro'
      ? {}
      : { 'se envolve terceiros': Boolean(values.reportedThirdParties) }) as Record<
      string,
      boolean
    >,
  } satisfies Record<string, Record<string, boolean>>;

  const statusOf = (checks: Record<string, boolean>): FormSectionStatus => {
    const missing = Object.keys(checks).filter((label) => !checks[label]);
    const total = Object.keys(checks).length;
    return { done: total - missing.length, total, missing };
  };
  const missing = Object.values(required).flatMap((checks) => statusOf(checks).missing!);

  const mutation = mode.kind === 'editar' ? update : create;
  const busy = mutation.isPending || filesBusy;
  const canSubmit = missing.length === 0 && !busy;

  const stored = mode.kind === 'editar' ? mode.claim.noticeAttachments : [];
  const fileItems = [
    ...stored
      .filter((d) => !removed.has(d.id))
      .map((d) => noticeAttachmentItem(mode.kind === 'editar' ? mode.claim.id : 0, d)),
    ...picked.items,
  ];
  const removeFile = (key: string) => {
    if (picked.items.some((i) => i.key === key)) picked.remove(key);
    else setRemoved((r) => new Set(r).add(Number(key)));
  };

  /**
   * Depois de o sinistro estar gravado: tira o que foi tirado e sobe o que foi
   * escolhido. O que falhar fica no formulário, com o motivo.
   */
  const finish = async (claim: Claim) => {
    setFilesBusy(true);
    setFilesError(null);
    try {
      for (const id of removed) await archiveNoticeFile(claim.id, id);
      setRemoved(new Set());
      await uploadNoticeFiles(claim.id, picked, picked.remove);
      setOpened(null);
      onSaved(claim);
    } catch (e) {
      setOpened(mode.kind === 'editar' ? null : claim);
      setFilesError(
        mode.kind === 'editar'
          ? `A correção foi salva, e um arquivo não: ${(e as Error).message}`
          : `O sinistro #${claim.id} foi aberto, e um arquivo não subiu: ${(e as Error).message} Tente de novo, ou siga sem ele — dá para enviar depois, pela correção.`,
      );
    } finally {
      setFilesBusy(false);
      /* A ficha e a lista foram atualizadas quando o sinistro gravou — antes
         dos arquivos. Sem esta segunda vez, o que acabou de subir não aparece. */
      void client.invalidateQueries({ queryKey: ['sinistros'] });
    }
  };

  const submit = () => {
    if (opened) {
      void finish(opened);
      return;
    }
    if (!canSubmit) return;
    const common = fields as Omit<ClaimFields, DriverKey>;
    const memberDriver = driver as Pick<ClaimFields, DriverKey>;
    if (mode.kind === 'editar') {
      update.mutate(
        {
          id: mode.claim.id,
          body: {
            ...common,
            ...driver,
            ...thirdPartyFields,
            ...(type === 'associado' ? { vehicleCode: vehicleCode.trim() } : {}),
            ...(type === 'terceiro'
              ? { thirdPartyId: thirdPartyVehicle.thirdPartyId!, thirdPartyVehicleId }
              : {}),
          },
        },
        { onSuccess: (claim) => void finish(claim) },
      );
    } else if (mode.kind === 'terceiro') {
      create.mutate(
        {
          type: 'terceiro',
          parentClaimId: mode.parent.id,
          thirdPartyId: thirdPartyVehicle.thirdPartyId!,
          thirdPartyVehicleId,
          ...(thirdPartyFields as Pick<ClaimFields, 'reportedThirdPartyVehicle'>),
          ...common,
        },
        { onSuccess: (claim) => void finish(claim) },
      );
    } else {
      create.mutate(
        {
          type: 'associado',
          vehicleCode: vehicleCode.trim(),
          ...memberDriver,
          ...(thirdPartyFields as Pick<ClaimFields, 'reportedThirdParties'>),
          ...common,
        },
        { onSuccess: (claim) => void finish(claim) },
      );
    }
  };

  return (
    <Form
      steps
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {mutation.error && <Alert tone="erro">{mutation.error.message}</Alert>}
      {filesError && <Alert tone="erro">{filesError}</Alert>}
      {mode.kind === 'terceiro' && (
        <Alert tone="informacao">
          O protocolo, o tipo, as datas e horas, o endereço e o reboque vieram do sinistro
          #{mode.parent.id} — confira e ajuste se preciso. A descrição e a dinâmica são a
          versão do terceiro, e começam vazias.
        </Alert>
      )}

      <FormSection
        icon={type === 'terceiro' ? 'terceiro' : 'veiculo'}
        status={statusOf(required.vehicle)}
        title={type === 'terceiro' ? 'O terceiro' : 'O veículo'}
        descricao={
          type === 'terceiro'
            ? `Quem é, e se envolve veículo — um carro e um muro são dois terceiros, cada um com o seu aviso. Fica ligado ao #${mode.kind === 'terceiro' ? mode.parent.id : mode.kind === 'editar' ? mode.claim.parentClaimId : ''}, do associado.`
            : 'O carro do associado, procurado no SGA. A placa e o dono são lidos do cadastro da casa, e não copiados.'
        }
      >
        {type === 'terceiro' ? (
          <>
            {/* Primeiro SE há veículo — é a resposta que decide o resto
                (`thirdPartyQuestions`): com carro, escolhe-se o carro e
                pergunta-se o seguro; sem ele, o tipo de dano. */}
            <FormRow cols={[6, 6]}>
              <Choice
                label="Envolve veículo de terceiro?"
                required
                value={values.reportedThirdPartyVehicle}
                onChange={set('reportedThirdPartyVehicle')}
                options={YES_NO}
              />
              {asks.asksInsurance ? (
                <Choice
                  label="O terceiro tem seguro?"
                  value={values.thirdPartyHasInsurance}
                  onChange={set('thirdPartyHasInsurance')}
                  options={YES_NO}
                />
              ) : asks.asksDamageType ? (
                <Choice
                  label="Tipo de dano ao terceiro"
                  required
                  value={values.thirdPartyDamageType}
                  onChange={set('thirdPartyDamageType')}
                  options={THIRD_PARTY_DAMAGE_OPTIONS}
                />
              ) : (
                <span />
              )}
            </FormRow>
            <ThirdPartyVehicleField
              required
              withVehicle={asks.asksVehicle}
              value={thirdPartyVehicle}
              onChange={setThirdPartyVehicle}
            />
          </>
        ) : (
          <ErpVehicleField value={vehicleCode} onChange={setVehicleCode} />
        )}
      </FormSection>

      <FormSection
        icon="relogio"
        status={statusOf(required.occurrence)}
        title="A ocorrência"
        descricao="Quando, o quê, e como a operação ficou sabendo."
      >
        <FormRow cols={[6, 6]}>
          <Field
            label="Protocolo OPA Suite"
            required
            autoComplete="off"
            value={values.opaSuiteProtocol}
            onChange={(e) => set('opaSuiteProtocol')(e.target.value)}
            placeholder="O protocolo do atendimento"
          />
          <Choice
            label="Tipo de sinistro"
            required
            value={values.claimType}
            onChange={set('claimType')}
            options={CLAIM_TYPE_OPTIONS}
          />
        </FormRow>
        <FormRow cols={[3, 3, 3, 3]}>
          <DateField
            label="Data do ocorrido"
            required
            value={values.occurredDate}
            onChange={set('occurredDate')}
            layer={layer}
          />
          <TimeField
            label="Hora do ocorrido"
            required
            value={values.occurredHour}
            onChange={set('occurredHour')}
            layer={layer}
          />
          <DateField
            label="Data do aviso"
            required
            value={values.notifiedDate}
            onChange={set('notifiedDate')}
            layer={layer}
          />
          <TimeField
            label="Hora do aviso"
            required
            value={values.notifiedHour}
            onChange={set('notifiedHour')}
            layer={layer}
          />
        </FormRow>
        <FormRow cols={[12]}>
          <TextArea
            label="Descrição do sinistro"
            required
            rows={4}
            value={values.description}
            onChange={(e) => set('description')(e.target.value)}
            placeholder="O que aconteceu, como a pessoa contou"
          />
        </FormRow>
      </FormSection>

      <FormSection
        icon="imagem"
        status={{ done: 0, total: 0 }}
        title="Imagens do ocorrido"
        descricao={
          canCreate
            ? 'As fotos e os vídeos do sinistro, ou qualquer arquivo que a pessoa mandou. Sobem depois de o sinistro ser gravado.'
            : 'As fotos e os vídeos do sinistro, ou qualquer arquivo que a pessoa mandou. Enviar pede a permissão de abrir sinistro; tirar, a de corrigir.'
        }
      >
        <AttachmentField
          label="Imagens do ocorrido"
          items={fileItems}
          onAdd={picked.add}
          onRemove={removeFile}
          disabled={busy || !canCreate}
        />
      </FormSection>

      <FormSection
        icon="mapa"
        status={statusOf(required.place)}
        title="O local"
        descricao="Onde aconteceu. O CEP preenche o resto, e o pino no mapa fica guardado como link."
      >
        <AddressField
          title="Endereço do ocorrido"
          required
          initialValue={source ? addressFrom(source) : undefined}
          onChange={setAddress}
          geocode={geocodeAddress}
        />
        <FormRow cols={[6, 6]}>
          <Field
            label="Sentido"
            autoComplete="off"
            value={values.direction}
            onChange={(e) => set('direction')(e.target.value)}
            placeholder="Sentido da via, se houver"
          />
          <Choice
            label="O local tem câmeras?"
            value={values.occurrenceHasCameras}
            onChange={set('occurrenceHasCameras')}
            options={CAMERAS_OPTIONS}
          />
        </FormRow>
      </FormSection>

      {asksDriver && (
        <FormSection
          icon="cracha"
          status={statusOf(required.driver)}
          title="O condutor"
          descricao="Quem dirigia na hora do sinistro."
        >
          <FormRow cols={[6]}>
            <Choice
              label="O condutor era o associado?"
              required
              value={values.driverIsMember}
              onChange={set('driverIsMember')}
              options={YES_NO}
            />
          </FormRow>
          {driverIsOther && (
            <FormRow cols={[4, 4, 4]}>
              <Field
                label="Nome do condutor"
                value={values.driverName}
                onChange={(e) => set('driverName')(e.target.value)}
                placeholder="Quem dirigia"
              />
              <Field
                label="CPF do condutor"
                mask="cpf"
                value={values.driverDocument}
                onChange={(e) => set('driverDocument')(e.target.value)}
              />
              <Field
                label="Telefone do condutor"
                type="tel"
                mask="phone"
                value={values.driverPhone}
                onChange={(e) => set('driverPhone')(e.target.value)}
              />
            </FormRow>
          )}
        </FormSection>
      )}

      <FormSection
        icon="demandas"
        status={statusOf(required.dynamics)}
        title="A dinâmica"
        descricao="Como aconteceu: quem causou, e se alguém de fora foi chamado."
      >
        <FormRow cols={[4, 4, 4]}>
          <Choice
            label="Causador do sinistro"
            required
            value={values.causedBy}
            onChange={set('causedBy')}
            options={CAUSED_BY_OPTIONS}
          />
          <Choice
            label="Houve avanço de preferencial?"
            required
            value={values.hadRightOfWayViolation}
            onChange={set('hadRightOfWayViolation')}
            options={YES_NO}
          />
          <Choice
            label="Algum órgão de trânsito foi acionado?"
            required
            value={values.trafficAuthorityNotified}
            onChange={set('trafficAuthorityNotified')}
            options={YES_NO}
          />
        </FormRow>
        {values.trafficAuthorityNotified === 'sim' && (
          <FormRow cols={[12]}>
            <Field
              label="Qual órgão foi acionado"
              value={values.trafficAuthorityDescription}
              onChange={(e) => set('trafficAuthorityDescription')(e.target.value)}
              placeholder="AMC, PRF, Polícia Militar…"
            />
          </FormRow>
        )}
      </FormSection>

      <FormSection
        icon="bandeira"
        status={statusOf(required.tow)}
        title="O reboque"
        descricao="Se o carro precisou ser levado, e para onde."
      >
        <FormRow cols={[6, 6]}>
          <Choice
            label="O associado acionou reboque?"
            required
            value={values.memberRequestedTow}
            onChange={set('memberRequestedTow')}
            options={YES_NO}
          />
          <Choice
            label="Destino do veículo"
            required
            value={values.towDestinationType}
            onChange={set('towDestinationType')}
            options={TOW_DESTINATION_OPTIONS}
          />
        </FormRow>
        {values.towDestinationType === 'outro_local_indicado_pelo_associado' && (
          <FormRow cols={[12]}>
            <Field
              label="Endereço de destino"
              value={values.towDestinationAddress}
              onChange={(e) => set('towDestinationAddress')(e.target.value)}
              placeholder="Para onde o carro foi levado"
            />
          </FormRow>
        )}
      </FormSection>

      {type !== 'terceiro' && (
        <FormSection
          icon="terceiro"
          status={statusOf(required.thirdParties)}
          title="O que foi declarado sobre terceiros"
          descricao="Só se há terceiro. O que é de cada um — se envolve veículo, o seguro, o tipo de dano — é respondido no aviso dele, aberto à parte pela ficha deste sinistro."
        >
          <FormRow cols={[6, 6]}>
            <Choice
              label="Envolve terceiros?"
              required
              value={values.reportedThirdParties}
              onChange={set('reportedThirdParties')}
              options={REPORTED_THIRD_PARTIES_OPTIONS}
            />
            <span />
          </FormRow>
          {asksThirdPartyNotices(values.reportedThirdParties) && (
            <Alert tone="alerta" title="Cada terceiro precisa do próprio aviso">
              {mode.kind === 'editar'
                ? 'Abra o sinistro de cada terceiro pela ficha deste, em "Adicionar terceiro envolvido".'
                : 'Depois de abrir este sinistro, abra o de cada terceiro pela ficha dele, em "Adicionar terceiro envolvido".'}{' '}
              Um carro e um muro são dois terceiros, cada um com o seu aviso.
            </Alert>
          )}
        </FormSection>
      )}

      <FormFooter>
        {opened ? (
          <Button type="button" variant="discreto" onClick={() => onSaved(opened)}>
            Seguir sem os arquivos
          </Button>
        ) : (
          <Button type="button" variant="discreto" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        {/* Habilitado mesmo com falta: clicar é o que leva à primeira etapa
            incompleta e diz o que falta nela. Desabilitado, ele só ficava
            cinza, sem dizer onde procurar. */}
        <Button type="submit" loading={busy} disabled={busy}>
          {opened
            ? 'Enviar os arquivos que faltaram'
            : mode.kind === 'editar'
              ? 'Salvar correção'
              : mode.kind === 'terceiro'
                ? 'Abrir sinistro do terceiro'
                : 'Abrir sinistro'}
        </Button>
      </FormFooter>
    </Form>
  );
}
